import { FC } from "react";
import { Checkbox, DatePicker, Form, InputNumber } from "antd";
import moment from "moment";

import { DealAnalysisOutput, NegotiationStatus } from "../../../../types/ProjectTypes";
import { NegotiationStatusSelect } from "./NegotiationStatusSelect";
import { VendorInput } from "../../../vendor/VendorInput";

type DealInfo = NonNullable<DealAnalysisOutput["dealInfo"]>;

interface DealInfoTabProps {
    output: DealAnalysisOutput;
    onChange: (output: DealAnalysisOutput) => void;
    /** The deal's negotiation status today, when the project already has a deal. */
    currentNegotiationStatus?: NegotiationStatus;
}

/**
 * Collect deal information from the superadmin and saves it on DealAnalysisOutput.
 */
export const DealInfoTab: FC<DealInfoTabProps> = ({
    output,
    onChange,
    currentNegotiationStatus,
}) => {
    const dealInfo = output.dealInfo;
    const update = (patch: Partial<DealInfo>) =>
        onChange({ ...output, dealInfo: { ...dealInfo, ...patch } as DealInfo });

    return (
        <Form labelCol={{ span: 6 }} wrapperCol={{ span: 12 }} layout="horizontal" size="small">
            <Form.Item label="Vendor" required>
                <VendorInput
                    key={dealInfo?.vendorKey || "empty"}
                    value={dealInfo?.vendorKey}
                    onSelect={(vendorKey) => update({ vendorKey })}
                />
            </Form.Item>
            <Form.Item label="Baseline TCV ($)" required>
                <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    value={dealInfo?.value != null ? dealInfo.value / 100 : undefined}
                    onChange={(value) => update({ value: Math.round((value || 0) * 100) })}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(value) => Number(value?.replace(/,/g, "") || 0)}
                />
            </Form.Item>
            <Form.Item label="Targeted savings ($)">
                <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    value={
                        dealInfo?.targetedSavings != null ? dealInfo.targetedSavings / 100 : undefined
                    }
                    onChange={(value) =>
                        update({
                            targetedSavings:
                                typeof value === "number" ? Math.round(value * 100) : undefined,
                        })
                    }
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(value) => Number(value?.replace(/,/g, "") || 0)}
                />
            </Form.Item>
            <Form.Item label="Realized savings ($)">
                <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    value={
                        dealInfo?.realizedSavings != null ? dealInfo.realizedSavings / 100 : undefined
                    }
                    onChange={(value) =>
                        update({
                            realizedSavings:
                                typeof value === "number" ? Math.round(value * 100) : undefined,
                        })
                    }
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(value) => Number(value?.replace(/,/g, "") || 0)}
                />
            </Form.Item>
            <Form.Item label="Start date">
                <DatePicker
                    style={{ width: "100%" }}
                    value={dealInfo?.orderStartDate ? moment(dealInfo.orderStartDate) : undefined}
                    onChange={(date) => update({ orderStartDate: date?.toISOString() })}
                />
            </Form.Item>
            <Form.Item label="End date">
                <DatePicker
                    style={{ width: "100%" }}
                    value={dealInfo?.orderEndDate ? moment(dealInfo.orderEndDate) : undefined}
                    onChange={(date) => update({ orderEndDate: date?.toISOString() })}
                />
            </Form.Item>
            <Form.Item label="Negotiation status">
                <NegotiationStatusSelect
                    value={dealInfo?.status}
                    current={currentNegotiationStatus}
                    onChange={(status) => update({ status })}
                />
            </Form.Item>
            <Form.Item label="Increase ($)">
                <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    value={dealInfo?.increase != null ? dealInfo.increase / 100 : undefined}
                    onChange={(value) => update({ increase: Math.round((value || 0) * 100) })}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(value) => Number(value?.replace(/,/g, "") || 0)}
                />
            </Form.Item>
            <Form.Item label="Auto renewal">
                <Checkbox
                    checked={dealInfo?.autoRenewal ?? false}
                    onChange={(e) => update({ autoRenewal: e.target.checked })}
                />
            </Form.Item>
        </Form>
    );
};
